import { isEmpty } from '~/_shared/utils/object/isEmpty';
import { removeUndefined } from '~/_shared/utils/object/pickBy';
import type { MapSettingsDataLayersResponse } from '~/map/map.repository';
import { getDefaultDataLayerSettings } from './mapSettingsDataLayers.factory';
import { mapSettingsDataLayersInitialState } from './mapSettingsDataLayers.reducer';
import type {
  DataLayerGroup, DataLayerIndividualBtgSettings, DataLayerIndividualSettings, MapSettingsDataLayersState,
} from './mapSettingsDataLayers.state';

export const mapSettingsDataLayersSerializer = {
  jsonToState: (json: MapSettingsDataLayersResponse): MapSettingsDataLayersState => {
    const { instances, individualLayerSettings, ...jsonRest } = json;

    return {
      ...mapSettingsDataLayersInitialState,
      ...jsonRest,
      instances: instances ? instances.map(mapSettingsDataLayersResponseInstanceToState) : [],
      individualLayerSettings: individualLayerSettings
        ? mapSettingsDataLayersResponseIndividualLayerSettingsToState(individualLayerSettings)
        : {},
    };
  },
  stateToJson: (state: MapSettingsDataLayersState): MapSettingsDataLayersResponse => {
    return state;
  },
};

export const mapSettingsDataLayersResponseInstanceToState = (
  instance: NonNullable<MapSettingsDataLayersResponse['instances']>[number],
): DataLayerGroup => ({
  ...instance,
  data: {
    ...instance.data,
  },
  settings: {
    ...getDefaultDataLayerSettings(),
    ...(instance.settings ? instance.settings : {}),
  },
});

export const mapSettingsDataLayersResponseIndividualLayerSettingsToState = (
  individualLayerSettings: NonNullable<MapSettingsDataLayersResponse['individualLayerSettings']>,
): MapSettingsDataLayersState['individualLayerSettings'] => {
  const result: Record<string, Record<string, DataLayerIndividualSettings>> = {};

  for (const [boundaryGroupId, demographicSettings] of Object.entries(individualLayerSettings)) {
    const convertedDemographicSettings: Record<string, DataLayerIndividualSettings> = {};

    for (const [demographicId, settings] of Object.entries(demographicSettings ?? {})) {
      const converted = mapSettingsDataLayersResponseIndividualSettingsToState(settings);

      if (converted) {
        convertedDemographicSettings[demographicId] = converted;
      }
    }

    if (Object.keys(convertedDemographicSettings).length > 0) {
      result[boundaryGroupId] = convertedDemographicSettings;
    }
  }

  return result;
};

const mapSettingsDataLayersResponseIndividualSettingsToState = (
  settings: Partial<DataLayerIndividualSettings> | undefined,
): DataLayerIndividualSettings | null => {
  const boundarySettings = settings?.boundarySettings;

  const converted = {
    individualSettingsActive: settings?.individualSettingsActive ?? false,
    boundarySettings: removeUndefined<DataLayerIndividualBtgSettings>({
      boundaryTerritories: boundarySettings?.boundaryTerritories,
      numberOfRanges: boundarySettings?.numberOfRanges,
      style: boundarySettings?.style && {
        gradientColors: boundarySettings.style.gradientColors,
      },
    }),
  } satisfies Partial<DataLayerIndividualSettings>;

  const empty = !converted.individualSettingsActive
    && isEmpty(converted.boundarySettings);

  return empty ? null : converted;
};
